<script>
import Multiselect from "vue-multiselect";
import Swal from "sweetalert2";
import { mapGetters } from "vuex";

import router from "@/router";
import ImageUpload from "@/components/image-upload";
import PhoneInput from "@/components/phone-input";

import { required, email, requiredIf, alphaNum } from "vuelidate/lib/validators";

export default {
  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      required: false,
    },
  },
  components: {
    Multiselect,
    ImageUpload,
    PhoneInput,
  },
  async mounted() {
    this.apiGet(
      `/advertisers`,
      { exclude: "agentImage,businessAddress,businessType,logoImage", limit: 100 },
      false
    )
      .then((data) => {
        if (this.isEdit) {
          const advertiserMap = new Map(
            data.data.map((advertiser) => [advertiser.id, advertiser])
          );

          this.user.advertisers.forEach((advertiser) => {
            if (!advertiserMap.has(advertiser.id)) {
              advertiserMap.set(advertiser.id, advertiser);
            }
          });

          this.advertiserOptions = Array.from(advertiserMap.values());
        } else {
          this.advertiserOptions = data.data;
        }
      })
      .catch((error) => {
        this.$store.dispatch("layout/addPageError", error);
      });

    this.apiGet(`/mastheads`, { exclude: "logoImage" }, true)
      .then((data) => {
        this.mastheadOptions = data.data;
      })
      .catch((error) => {
        this.$store.dispatch("layout/addPageError", error);
      });

    this.apiGet(`/users`, { exclude: "profileImage" }, true)
      .then((data) => {
        this.userOptions = data.data;
      })
      .catch((error) => {
        this.$store.dispatch("layout/addPageError", error);
      });

    if (this.user) {
      delete this.user.id;
      delete this.user.updatedAt;
      delete this.user.createdAt;

      if (this.user.profileImage) {
        this.avatarImageDefault.url = this.user.profileImage.url;
        this.avatarImageDefault.filename = this.user.profileImage.filename;

        this.form.profileImage = this.user.profileImage.id;

        delete this.user.profileImage;
      }

      if (this.user.parent) {
        if (this.user.parent.id !== this.currentUser.id) {
          this.form.parent = this.user.parent.id;
        }

        delete this.user.parent;
      }

      Object.assign(this.form, this.user);

      delete this.form.deletedAt;

      this.form.advertisers =
        this.user.advertisers && this.user.advertisers.length
          ? this.user.advertisers.map((advertiser) => advertiser.id)
          : [];

      this.form.mastheads =
        this.form.mastheads && this.user.mastheads.length
          ? this.user.mastheads.map((masthead) => masthead.id)
          : [];

      this.editData = true;
    }
  },
  data() {
    return {
      editData: false,
      submitted: false,
      creating: false,
      hasError: false,
      errors: null,
      type: null,
      avatarDropzoneOptions: {
        thumbnailWidth: 150,
        maxFilesize: 10,
      },
      roles: [
        { name: "Admin", value: "ROLE_ADMIN" },
        { name: "Sales Director", value: "ROLE_SALES_DIRECTOR" },
        { name: "Sales Manager", value: "ROLE_SALES_MANAGER" },
        { name: "Sales Representative", value: "ROLE_SALES_REPRESENTATIVE" },
      ],
      userOptions: [],
      advertiserOptions: [],
      mastheadOptions: [],
      form: {
        email: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        profileImage: "",
        pongrassSalesRepCodeEid: "",
        role: "",
        advertisers: [],
        parent: "",
        mastheads: [],
        receiveEmails: true,
      },
      avatarImageDefault: {
        url: "",
        filename: "",
      },
    };
  },
  validations: {
    form: {
      firstName: { required },
      lastName: { required },
      email: { email, required },
      role: { required },
      pongrassSalesRepCodeEid: { alphaNum },
      parent: {
        required: requiredIf(function () {
          return (
            this.form.role &&
            this.currentUser.role === "ROLE_SALES_DIRECTOR" &&
            this.form.role === "ROLE_SALES_REPRESENTATIVE"
          );
        }),
      },
      mastheads: {
        required: requiredIf(function () {
          return this.form.role && this.form.role === "ROLE_SALES_DIRECTOR";
        }),
      },
    },
  },
  methods: {
    handleCreate() {
      this.submitted = true;

      this.$v.form.$touch();

      if (!this.$v.form.$invalid) {
        this.type = "create";
        this.creating = true;

        this.apiPost(`/users`, { ...this.form })
          .then(() => {
            this.creating = false;

            Swal.fire({
              title: `You have successfully added ${this.form.firstName} ${this.form.lastName} as a user`,
              icon: "success",
              confirmButtonText: "Back Home",
              confirmButtonColor: "#1D99C7",
              allowOutsideClick: false,
              allowEscapeKey: false,
            }).then((result) => {
              if (result.value) {
                router.push({ path: "/users" });
              }
            });
          })
          .catch((error) => {
            this.creating = false;

            if (error) {
              this.hasError = true;
              this.errors = error.response ? error.response.data.error.message : null;
            }
          });
      }
    },
    handleUpdate() {
      this.submitted = true;

      this.$v.form.$touch();

      if (!this.$v.form.$invalid) {
        this.type = "update";
        this.creating = true;

        const form = { ...this.form };

        delete form.email;
        delete form.fullName;

        this.apiPatch(`/users/${this.$route.params.id}`, { ...form })
          .then(() => {
            this.creating = false;

            Swal.fire({
              title: `You have updated ${this.form.firstName} ${this.form.lastName}`,
              icon: "success",
              confirmButtonText: "Back Home",
              confirmButtonColor: "#1D99C7",
              allowOutsideClick: false,
              allowEscapeKey: false,
            }).then((result) => {
              if (result.value) {
                router.push({ path: "/users" });
              }
            });
          })
          .catch((error) => {
            this.creating = false;

            if (error) {
              this.hasError = true;
              this.errors = error.response ? error.response.data.error.message : null;
            }
          });
      }
    },
    handleImageUpload(id) {
      this.form.profileImage = id;
    },
    handleDeleteImage() {
      this.form.profileImage = null;
    },
    handleAdvertisersSelect({ id }) {
      this.form.advertisers.push(id);
    },
    handleRoleSelect({ value }) {
      if (value === "ROLE_SALES_DIRECTOR") {
        this.mastheadOptions.forEach(({ id }) => {
          this.form.mastheads.push(id);
        });
      } else {
        this.form.mastheads = [];
      }

      if (value !== "ROLE_SALES_MANAGER" && value !== "ROLE_SALES_REPRESENTATIVE") {
        this.form.advertisers = [];
      }

      this.form.parent = "";

      this.form.role = value;
    },
    handleAdvertisersRemove({ id }) {
      let index = this.form.advertisers.indexOf(id);

      if (index !== -1) {
        this.form.advertisers.splice(index, 1);
      }
    },
    handleParentSelect({ id }) {
      this.form.parent = id;
    },
    handleMastheadSelect({ id }) {
      this.form.mastheads.push(id);
    },
    handleMastheadRemove({ id }) {
      let index = this.form.mastheads.indexOf(id);

      if (index !== -1) {
        this.form.mastheads.splice(index, 1);
      }
    },

    handleAdvertisersSearch(searchTerm) {
      const params = {
        exclude: "agentImage,businessAddress,businessType,logoImage",
        limit: 100,
      };

      if (searchTerm && searchTerm.length > 2) {
        params.searchString = searchTerm;
      }

      this.apiGet(`/advertisers`, params, false)
        .then((data) => {
          const advertiserMap = new Map();

          if (this.isEdit && this.user.advertisers) {
            this.user.advertisers.forEach((advertiser) => {
              advertiserMap.set(advertiser.id, advertiser);
            });
          }

          data.data.forEach((advertiser) => {
            advertiserMap.set(advertiser.id, advertiser);
          });

          this.advertiserOptions = Array.from(advertiserMap.values());
        })
        .catch((error) => {
          console.error("Failed to fetch advertisers:", error);
        });
    },
  },
  computed: {
    roleValue() {
      return this.roles ? this.roles.find((role) => role.value === this.form.role) : [];
    },
    advertisersValue() {
      return this.advertiserOptions
        ? this.advertiserOptions.filter((advertiser) =>
            this.form.advertisers.includes(advertiser.id)
          )
        : [];
    },
    parentValue() {
      return this.userOptions
        ? this.userOptions.find((user) => user.id === this.form.parent)
        : [];
    },
    mastheadValue() {
      return this.mastheadOptions
        ? this.mastheadOptions.filter((masthead) =>
            this.form.mastheads.includes(masthead.id)
          )
        : [];
    },
    roleOptions() {
      return this.roles.length && this.currentUser.rolesCanCreate
        ? this.roles.filter((role) =>
            this.currentUser.rolesCanCreate.includes(role.value)
          )
        : [];
    },
    parentOptions() {
      if (this.form.role) {
        if (
          (this.currentUser.role === "ROLE_ADMIN" ||
            this.currentUser.role === "ROLE_SUPER_ADMIN") &&
          this.form.role === "ROLE_SALES_MANAGER"
        ) {
          return this.userOptions
            ? this.userOptions.filter((user) => user.role === "ROLE_SALES_DIRECTOR")
            : [];
        } else {
          return this.userOptions
            ? this.userOptions.filter((user) => user.role === "ROLE_SALES_MANAGER")
            : [];
        }
      } else {
        return [];
      }
    },
    showParent() {
      if (this.form.role) {
        if (
          (this.currentUser.role === "ROLE_SALES_DIRECTOR" ||
            this.currentUser.role === "ROLE_SUPER_ADMIN" ||
            this.currentUser.role === "ROLE_ADMIN") &&
          this.form.role === "ROLE_SALES_REPRESENTATIVE"
        ) {
          return true;
        } else if (
          (this.currentUser.role === "ROLE_SUPER_ADMIN" ||
            this.currentUser.role === "ROLE_ADMIN") &&
          this.form.role === "ROLE_SALES_MANAGER"
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    },
    ...mapGetters("auth", ["currentUser"]),
  },
};
</script>

<template>
  <div class="row">
    <div class="col-12 col-md-3">
      <div class="card">
        <div class="card-body">
          <h3>System User</h3>
          <p>
            Invite and Manage Users to the Advantage Platform. Assign Permissions and User
            Access Levels. Assign Advertiser Profiles to more than One Sales Rep. Update
            your Sales Rep Code.
          </p>
        </div>
      </div>
    </div>
    <div class="col-12 col-md-9">
      <div class="card">
        <div class="card-body">
          <div class="row">
            <div class="col-12 col-md-4">
              <b-form-group>
                <label>Avatar Image</label>
                <image-upload
                  crop-required
                  :aspect-ratio="1 / 1"
                  round
                  v-if="!isEdit || editData"
                  :dropzone-options="avatarDropzoneOptions"
                  @image-upload="({ id }) => handleImageUpload(id)"
                  @delete-image="handleDeleteImage"
                  name="avatarImage"
                  dropzone-label="Upload User Avatar Image"
                  :display-image="avatarImageDefault"
                />
              </b-form-group>
            </div>
          </div>

          <div class="row">
            <div class="col-12 col-md-6">
              <b-form-group>
                <label for="firstName">First Name</label>
                <b-input
                  id="firstName"
                  name="firstName"
                  placeholder="First Name"
                  type="text"
                  v-model="form.firstName"
                  :class="{ 'is-invalid': submitted && $v.form.firstName.$error }"
                />
                <div
                  v-if="submitted && $v.form.firstName.$error"
                  class="invalid-feedback"
                >
                  <span v-if="!$v.form.firstName.required">This value is required.</span>
                </div>
              </b-form-group>
            </div>
            <div class="col-12 col-md-6">
              <b-form-group>
                <label for="lastName">Last Name</label>
                <b-input
                  id="lastName"
                  name="lastName"
                  placeholder="Last Name"
                  type="text"
                  v-model="form.lastName"
                  :class="{ 'is-invalid': submitted && $v.form.lastName.$error }"
                />
                <div v-if="submitted && $v.form.lastName.$error" class="invalid-feedback">
                  <span v-if="!$v.form.lastName.required">This value is required.</span>
                </div>
              </b-form-group>
            </div>
          </div>

          <div class="row">
            <div class="col-12 col-md-6">
              <b-form-group>
                <label for="phoneNumber">Contact Number</label>
                <phone-input
                  id="phoneNumber"
                  name="phoneNumber"
                  placeholder="Contact Number"
                  v-model="form.phoneNumber"
                />
              </b-form-group>
            </div>
            <div class="col-12 col-md-6">
              <b-form-group>
                <label for="email">Email Address</label>
                <b-input
                  id="email"
                  name="email"
                  placeholder="Email Address"
                  type="email"
                  v-model="form.email"
                  :disabled="editData"
                  :class="{ 'is-invalid': submitted && $v.form.email.$error }"
                />
                <div v-if="submitted && $v.form.email.$error" class="invalid-feedback">
                  <span v-if="!$v.form.email.required">This value is required.</span>
                  <span v-if="!$v.form.email.email">Please enter valid email.</span>
                </div>
              </b-form-group>
            </div>
          </div>

          <div class="row">
            <div class="col-12 col-md-4">
              <label for="role">User Role</label>
              <multiselect
                :value="roleValue"
                @select="handleRoleSelect"
                label="name"
                track-by="value"
                name="role"
                id="role"
                :options="roleOptions"
                :class="{ 'is-invalid': submitted && $v.form.role.$error }"
                select-label=""
                deselect-label=""
              ></multiselect>
              <div v-if="submitted && $v.form.role.$error" class="invalid-feedback">
                <span v-if="!$v.form.role.required">This value is required.</span>
              </div>
            </div>
            <div class="col-12 col-md-4">
              <label for="parent">Parent</label>
              <multiselect
                :value="parentValue"
                @select="handleParentSelect"
                label="fullName"
                track-by="id"
                name="parent"
                id="parent"
                :options="parentOptions"
                :class="{ 'is-invalid': submitted && $v.form.parent.$error }"
                select-label=""
                deselect-label=""
                :disabled="!showParent"
              ></multiselect>
              <div v-if="submitted && $v.form.parent.$error" class="invalid-feedback">
                <span v-if="!$v.form.parent.required">This value is required.</span>
              </div>
            </div>
            <div class="col-12 col-md-4">
              <b-form-group>
                <div class="d-flex align-item-center justify-content-between">
                  <label for="repCode">Rep Code</label>
                  <b-button
                    variant="link"
                    class="p-0"
                    v-b-popover.left="
                      'Please add your Sales Rep Code to ensure alignment of reporting and ability to book a campaign.'
                    "
                  >
                    <i class="bx bxs-info-circle font-size-24"></i>
                  </b-button>
                </div>
                <b-input
                  id="repCode"
                  name="repCode"
                  placeholder="Rep Code"
                  type="text"
                  v-model="form.pongrassSalesRepCodeEid"
                  :class="{
                    'is-invalid': submitted && $v.form.pongrassSalesRepCodeEid.$error,
                  }"
                />
                <div
                  v-if="submitted && $v.form.pongrassSalesRepCodeEid.$error"
                  class="invalid-feedback"
                >
                  <span class="d-block" v-if="!$v.form.pongrassSalesRepCodeEid.alphaNum"
                    >Please enter a alphanumeric value.</span
                  >
                </div>
              </b-form-group>
            </div>
          </div>

          <div class="row">
            <div
              class="col-12"
              v-if="this.form.role && this.form.role === 'ROLE_SALES_DIRECTOR'"
            >
              <label for="parent">Masthead</label>
              <multiselect
                :multiple="true"
                :value="mastheadValue"
                @select="handleMastheadSelect"
                @remove="handleMastheadRemove"
                class="max-height"
                label="name"
                track-by="id"
                name="masthead"
                id="masthead"
                :options="mastheadOptions"
                :class="{ 'is-invalid': submitted && $v.form.mastheads.$error }"
                select-label=""
                deselect-label=""
              ></multiselect>
              <div v-if="submitted && $v.form.mastheads.$error" class="invalid-feedback">
                <span v-if="!$v.form.mastheads.required">This value is required.</span>
              </div>
            </div>
          </div>

          <div class="row">
            <div
              class="col-12"
              v-if="this.form.role && this.form.role !== 'ROLE_SALES_DIRECTOR'"
            >
              <label for="advertisers">Advertisers</label>
              <multiselect
                :multiple="true"
                :value="advertisersValue"
                @select="handleAdvertisersSelect"
                @remove="handleAdvertisersRemove"
                @search-change="handleAdvertisersSearch"
                name="advertisers"
                label="businessName"
                track-by="id"
                id="advertisers"
                :options="advertiserOptions"
                select-label=""
                deselect-label=""
                searchable
              ></multiselect>
            </div>
          </div>
        </div>
        <div class="card-footer card-footer--border bg-white">
          <b-alert v-model="hasError" variant="danger" dismissible
            >Error - We are unable to {{ type }} this User due to: {{ errors }}</b-alert
          >
          <div class="d-flex justify-content-end">
            <b-button
              v-if="!isEdit || !user.deletedAt"
              :disabled="creating"
              variant="primary"
              class="w-md"
              @click="isEdit ? handleUpdate() : handleCreate()"
            >
              {{ isEdit ? "Update" : "Create User" }}
            </b-button>
            <p v-if="isEdit && user.deletedAt" class="mb-0 font-italic text-primary py-2">
              Archived users can't be updated, please unarchive this user to update.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
